import { getContextId, InlineMarkdown } from '@tlx/astro-shared';
import DOMPurify from 'dompurify';
import { useMemo } from 'react';

export function addContextId(url: string, contextId: string) {
    const urlObj = new URL(url, window.location.origin);
    if (urlObj.origin === window.location.origin && !urlObj.searchParams.has('contextId')) {
        urlObj.searchParams.set('contextId', contextId);
    }
    return urlObj.toString();
}

const ALLOWED_DOMAINS = ['altinn.no', 'tripletex.no', 'skatteetaten.no'];

// We remove all DOMPurify hooks first, to prevent duplicate hook registrations.
// (Duplication can happen in HMR during development.)
DOMPurify.removeAllHooks();
DOMPurify.addHook('beforeSanitizeAttributes', function (node) {
    if (node instanceof HTMLAnchorElement) {
        node.removeAttribute('class');
        node.removeAttribute('id');
        node.removeAttribute('tabindex');

        // Regex: replace javascript:nav.link(...)
        // Examples:
        // javascript:nav.link('/execute/inboxVoucher?mode=1')
        // javascript:nav.link(/execute/updateCompany, _blank)
        // javascript:nav.link('/execute/updateCompany', '_blank', 'someFragment')
        if (node.href.startsWith('javascript:')) {
            let replaced = decodeURIComponent(node.href)
                .replace(/^javascript:nav\.link\('(.*?)', '(.*?)', '(.*?)'\)$/, (_, url, target, fragment) => {
                    if (target === '_blank') {
                        node.target = '_blank';
                    }
                    return url + '#' + fragment;
                })
                .replace(/^javascript:nav\.link\((.*?),\s*(?:['"&quot;])?_blank(?:['"&quot;])?\)$/, (_, url) => {
                    node.target = '_blank';
                    // Clean up any quotes, apostrophes, or spaces from the URL
                    return url.replace(/^['"]/, '').replace(/[,'"&quot;\s]+$/, '');
                })
                .replace(/^javascript:nav\.link\('(.*?)'\)$/, '$1')
                .replace(/^javascript:nav\.link\((.*?)\)$/, '$1');

            const contextId = getContextId();
            if (contextId !== null) {
                replaced = addContextId(replaced, contextId);
            }
            node.href = replaced;
        }

        // Check if the URL is external, and if so, remove the href attribute.
        // We only want to support links to internal pages.
        const currentOrigin = window.location.origin;
        const linkUrl = new URL(node.href, currentOrigin);
        const linkOrigin = linkUrl.origin;

        const isOwnDomain = linkOrigin === currentOrigin;

        // If somebody tries to open a link in for example an iframe, we disallow that
        if (node.target !== '' && node.target !== '_blank' && node.target !== '_self') {
            node.target = '_blank';
        }

        // Check for tripletex.no, www.tripletex.no, hjelp.tripletex.no and so on
        const isAllowedExternalUrl = ALLOWED_DOMAINS.some(
            (domain) => linkUrl.host === domain || linkUrl.host.endsWith('.' + domain),
        );

        if (!isOwnDomain && !isAllowedExternalUrl) {
            node.removeAttribute('href');
            node.tabIndex = -1;
            node.dataset.disabledLink = 'true';
        }
    } else if (node instanceof HTMLDivElement) {
        if (node.getAttribute('id')?.startsWith('tourme-button-') && node.classList.contains('tourme')) {
            /* empty */
        } else {
            node.removeAttribute('id');
            node.removeAttribute('class');
            node.removeAttribute('tabindex');
        }
    }
});

function convertHTMLtoMarkdown(text: string) {
    return text
        .replace(/<p>/g, '')
        .replace(/<\/p>/g, '\n')
        .replace(/<br\s*\/?>/g, '\n')
        .replace(/<div>/g, '')
        .replace(/<\/div>/g, '\n')
        .replace(/<em>/g, '*')
        .replace(/<\/em>/g, '*')
        .replace(/<a[^>]*href="([^"]+)"[^>]*>([^<]+)<\/a>/g, '[$2]($1)')
        .replace(/<a(?![^>]*href=)[^>]*>([^<]+)<\/a>/g, '$1');
}

export function SanitizedHTMLAndMarkdown({ content }: { content: string }) {
    const sanitizedHtml = useMemo(
        () =>
            DOMPurify.sanitize(content, {
                ALLOWED_TAGS: ['a', 'br', 'p', 'div', 'em'],
                ALLOWED_ATTR: ['href', 'target', 'id', 'class', 'tabindex'],
            }),
        [content],
    );
    const markdown = convertHTMLtoMarkdown(sanitizedHtml);

    return <InlineMarkdown>{markdown}</InlineMarkdown>;
}
